import {
  SensitivityAnalysisOutput,
  UnitRateModelFeatureKey,
  defaultUnitRateModelFeatureKeys,
  orUndefined,
  schUnitRateModels,
} from '@aloft/toolkit-shared';
import { Flex, Icon, SimpleSelect, Typography, colors } from '@aloft/toolkit-sui';
// eslint-disable-next-line aloft/no-direct-chakra, aloft/prefer-sound-ui-button, import/no-extraneous-dependencies
import {
  Modal as ChakraModal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
} from '@chakra-ui/react';

import { useMemo, useState } from 'react';
import { useParams } from 'react-router';

import { PropertyGrid } from '../../../components/PropertyGrid/PropertyGrid.tsx';
import { PropertyGridSensitivityContext } from '../../../components/PropertyGrid/PropertyGridSensitivityContext.tsx';
import { SensitivityRateSection } from '../../../components/PropertyGrid/columnSections/SensitivityRateSection.tsx';
import { SensitivityUnitRateLookupSection } from '../../../components/PropertyGrid/columnSections/SensitivityUnitRateLookupSection.tsx';
import { useGetTkv2UnitRateModelsAPI } from '../../../hooks/api/toolkitComponents.ts';
import { SensitivityModalGraphSection } from './SensitivityModalGraphSection.tsx';
import { StatsRow } from './StatsRow.tsx';

const SENSITIVITY_WARNING_THRESHOLD_POOR = 0.15;
const SENSITIVITY_WARNING_THRESHOLD_ACCEPTABLE = 0.1;

const getSensitivityWarningMessage = (spreadRatio: number): string | undefined => {
  if (spreadRatio > SENSITIVITY_WARNING_THRESHOLD_POOR) {
    return 'Percent Variance exceeds 15% at the optimal rate. This indicates the model is unable to effectively reduce the adjusted sale price spread for this feature, and requires manual adjustment.';
  }
  if (spreadRatio > SENSITIVITY_WARNING_THRESHOLD_ACCEPTABLE) {
    return 'Percent Variance between 10% and 15% indicates a moderate reduction in the adjusted sale price spread. While this reflects an acceptable market signal, the sensitivity rate should be used with caution and supported by supplemental valuation analysis.';
  }
  return undefined;
};

const SensitivityWarningMessage = ({ spreadRatio }: { spreadRatio: number }) => {
  const message = getSensitivityWarningMessage(spreadRatio);
  if (!message) return null;

  const isPoor = spreadRatio > SENSITIVITY_WARNING_THRESHOLD_POOR;
  const color = isPoor ? 'textError' : 'textWarning';

  return (
    <Flex alignItems="center" gap="0.5rem" paddingRight="1rem">
      <Icon icon="error-warning" size="md" color={color} />
      <Typography variant="label" __dangerDoNotUseSx={{ maxWidth: 'none' }}>
        {message}
      </Typography>
    </Flex>
  );
};

const featureLabels: Record<UnitRateModelFeatureKey, string> = {
  gla: 'Gross Living Area',
  bedrooms: 'Bedrooms',
  fullBaths: 'Full Baths',
  halfBaths: 'Half Baths',
  garageCarport: 'Garage / Carport',
  actualAge: 'Actual Age',
  siteSize: 'Site Size',
};

export const SensitivityAnalysisModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const orderID = useParams().orderId!;

  const [selectedFeatureKey, setSelectedFeatureKey] = useState<UnitRateModelFeatureKey>('gla');

  const unitRateModelsQuery = useGetTkv2UnitRateModelsAPI(
    { pathParams: { orderID } },
    { staleTime: Infinity },
  );

  const sensitivityAnalysis: SensitivityAnalysisOutput | undefined = orUndefined(
    schUnitRateModels.parse,
  )(unitRateModelsQuery.data?.body)?.sensitivityAnalysis;

  const selectedResult = sensitivityAnalysis?.[selectedFeatureKey];

  const sensitivityContextValue = useMemo(
    () => ({ sensitivityResult: selectedResult, sensitivityFeatureKey: selectedFeatureKey }),
    [selectedResult, selectedFeatureKey],
  );

  return (
    <ChakraModal isOpen={isOpen} onClose={onClose} scrollBehavior="inside" size="6xl" isCentered>
      <ModalOverlay />
      <ModalContent bg={colors.surface3} onKeyDown={(e) => e.stopPropagation()}>
        <ModalCloseButton />
        <ModalBody padding="1.5rem">
          <Flex flexDirection="column" gap="1rem">
            <Flex alignItems="center" gap="0.75rem">
              <Icon icon="speedometer" size="xl" />
              <Typography variant="h1">Sensitivity Analysis Model</Typography>
            </Flex>

            <Flex flexDirection="column" gap="0.25rem">
              <Typography
                variant="body2"
                color="textSecondary"
                __dangerDoNotUseSx={{ maxWidth: 'none' }}
              >
                Toolkit&apos;s sensitivity model is built using an assumption on the most likely
                values for a given variable. Select a variable to see the analysis details:
              </Typography>
              {selectedResult && !selectedResult.selectedRate.reliable && (
                <SensitivityWarningMessage spreadRatio={selectedResult.selectedRate.spreadRatio} />
              )}
            </Flex>

            {sensitivityAnalysis ? (
              <>
                <Flex alignItems="center" gap="0.5rem" width="fit-content">
                  <Typography variant="body2" color="textSecondary">
                    Variable:
                  </Typography>
                  <SimpleSelect
                    size="sm"
                    value={selectedFeatureKey}
                    onChange={(e) =>
                      setSelectedFeatureKey(e.target.value as UnitRateModelFeatureKey)
                    }
                  >
                    {defaultUnitRateModelFeatureKeys.map((key) => (
                      <option key={key} value={key}>
                        {featureLabels[key]}
                      </option>
                    ))}
                  </SimpleSelect>
                </Flex>

                {selectedResult && (
                  <>
                    <Flex justifyContent="center">
                      <StatsRow type="sensitivity" result={selectedResult} />
                    </Flex>
                    <SensitivityModalGraphSection
                      result={selectedResult}
                      featureKey={selectedFeatureKey}
                    />
                  </>
                )}

                <Flex overflowX="auto" width="100%">
                  <PropertyGridSensitivityContext.Provider value={sensitivityContextValue}>
                    <PropertyGrid
                      gridType="SensitivityGrid"
                      columnSections={[SensitivityUnitRateLookupSection, SensitivityRateSection]}
                      labelColumnHeader={
                        <Flex
                          width="100%"
                          alignItems="center"
                          gap="0.5rem"
                          padding="0.5rem"
                          backgroundColor="surface4"
                        >
                          <Typography variant="label" color="textSecondary">
                            Property
                          </Typography>
                        </Flex>
                      }
                    />
                  </PropertyGridSensitivityContext.Provider>
                </Flex>
              </>
            ) : (
              <Typography variant="body2" color="textSecondary">
                Sensitivity analysis data is not available.
              </Typography>
            )}
          </Flex>
        </ModalBody>
      </ModalContent>
    </ChakraModal>
  );
};
